import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  OnInit,
  OnDestroy,
} from '@angular/core';

@Directive({
  selector: '[uiFileImport]',
  standalone: true,
})
export class UIFileImportDirective implements OnInit, OnDestroy {
  @Input() accept = '*/*'; // File types to accept (e.g., '.pdf,.doc,.docx', 'image/*')
  @Input() multiple = false; // Allow multiple file selection
  @Input() maxSize = 10 * 1024 * 1024; // Max file size in bytes (default: 10MB)
  @Input() disabled = false; // Disable the import functionality

  @Output() fileSelected = new EventEmitter<File[]>();
  @Output() fileError = new EventEmitter<string>();

  private fileInput?: HTMLInputElement;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit() {
    this.createFileInput();
    this.setupClickListener();
  }

  private createFileInput() {
    this.fileInput = this.renderer.createElement('input');
    this.renderer.setAttribute(this.fileInput, 'type', 'file');
    this.renderer.setAttribute(this.fileInput, 'accept', this.accept);
    this.renderer.setStyle(this.fileInput, 'display', 'none');

    if (this.multiple) {
      this.renderer.setAttribute(this.fileInput, 'multiple', 'true');
    }

    // Add to document body
    this.renderer.appendChild(document.body, this.fileInput);

    // Listen for file selection
    this.renderer.listen(this.fileInput, 'change', event => {
      this.handleFileSelection(event);
    });
  }

  private setupClickListener() {
    this.renderer.listen(this.el.nativeElement, 'click', event => {
      event.preventDefault();
      if (!this.disabled) {
        this.fileInput?.click();
      }
    });
  }

  private handleFileSelection(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (!files || files.length === 0) {
      return;
    }

    const fileArray = Array.from(files);
    const validFiles: File[] = [];

    // Validate each file
    for (const file of fileArray) {
      if (file.size > this.maxSize) {
        this.fileError.emit(
          `File "${file.name}" exceeds maximum size of ${this.formatFileSize(this.maxSize)}`,
        );
        continue;
      }

      if (!this.isValidFileType(file)) {
        this.fileError.emit(`File "${file.name}" is not a valid file type`);
        continue;
      }

      validFiles.push(file);
    }

    // Emit valid files if any
    if (validFiles.length > 0) {
      this.fileSelected.emit(validFiles);
    }

    // Reset input value to allow selecting the same file again
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  private isValidFileType(file: File): boolean {
    if (this.accept === '*/*') {
      return true;
    }

    const acceptedTypes = this.accept.split(',').map(type => type.trim());

    return acceptedTypes.some(acceptedType => {
      if (acceptedType.startsWith('.')) {
        // Extension check
        return file.name.toLowerCase().endsWith(acceptedType.toLowerCase());
      } else if (acceptedType.includes('/*')) {
        // MIME type wildcard (e.g., image/*)
        const baseType = acceptedType.split('/')[0];
        return file.type.startsWith(baseType + '/');
      } else {
        // Exact MIME type match
        return file.type === acceptedType;
      }
    });
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  ngOnDestroy() {
    // Clean up the hidden file input
    if (this.fileInput && this.fileInput.parentNode) {
      this.fileInput.parentNode.removeChild(this.fileInput);
    }
  }
}
